import React, { useState, useEffect } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { useLocation, useNavigate } from "react-router-dom";
import { auth, firestore as db, database as realtimeDb } from "../FirebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import { ref, get } from "firebase/database";
import axios from "axios";
import toast from "react-hot-toast";

const API_URL = "https://us-central1-tghc-web.cloudfunctions.net/";

const Checkout = () => {
  const [user, setUser] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [productDetails, setProductDetails] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(-1);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [loading, setLoading] = useState(true);
  const [placingOrder, setPlacingOrder] = useState(false); // ✅ new state

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const productId = queryParams.get("productId");
  const quantity = queryParams.get("quantity");

  const fetchSingleProductDetails = async (productId, quantity) => {
    try {
      const productRef = doc(db, "products", productId);
      const productSnap = await getDoc(productRef);
      if (productSnap.exists()) {
        const productData = productSnap.data();
        const discountPrice = productData.prices.productPrice - productData.prices.productDiscount;
        setProductDetails([{ ...productData, quantity, discountPrice }]);
      }
    } catch (error) {
      console.error("Error fetching product details:", error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await fetchAddresses(currentUser.uid);
        await fetchDeliveryFee();
        if (productId) {
          await fetchSingleProductDetails(productId, parseInt(quantity));
        } else {
          await fetchCartItems(currentUser.uid);
        }
      } else {
        navigate(`/login?redirect=/checkout${productId ? `&productId=${productId}&quantity=${quantity}` : ""}`);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate, productId, quantity]);

  const fetchCartItems = async (userId) => {
    const cartRef = doc(db, "customers", userId);
    const cartSnap = await getDoc(cartRef);
    if (cartSnap.exists()) {
      const cartData = cartSnap.data().items || [];
      setCartItems(cartData);
      fetchProductDetails(cartData);
    }
  };

  const fetchProductDetails = async (cartItems) => {
    if (!cartItems.length) return;
    try {
      const productPromises = cartItems.map(async (cartItem) => {
        const productRef = doc(db, "products", cartItem.productId);
        const productSnap = await getDoc(productRef);
        if (productSnap.exists()) {
          const productData = productSnap.data();
          const discountPrice = productData.prices.productPrice - productData.prices.productDiscount;
          return {
            ...productData,
            quantity: cartItem.quantity,
            discountPrice,
          };
        }
        return null;
      });
      const products = await Promise.all(productPromises);
      setProductDetails(products.filter(Boolean));
    } catch (error) {
      console.error("Error fetching product details:", error);
    }
  };

  const fetchAddresses = async (userId) => {
    const userRef = doc(db, "customers", userId);
    const userSnap = await getDoc(userRef);
    if (userSnap.exists()) {
      setAddresses(userSnap.data().address || []);
      setSelectedAddress(userSnap.data().defaultAddress ?? -1);
    }
  };

  const fetchDeliveryFee = async () => {
    const feeRef = ref(realtimeDb, "config/deliveryFee");
    const feeSnap = await get(feeRef);
    if (feeSnap.exists()) {
      setDeliveryFee(feeSnap.val() || 0);
    }
  };

  const handleCheckout = async (productId = null, quantity = null) => {
    if (!user) return alert("Please log in first.");
    if (selectedAddress === -1) return toast.error("Please select an address.");
    if (placingOrder) return;

    setPlacingOrder(true);

    try {
      const token = await user.getIdToken(true);
      const checkoutType = productId ? "direct" : "cart";

      const payload = {
        checkoutType,
        address: addresses[selectedAddress],
      };

      if (checkoutType === "direct") {
        payload.productId = productId;
        payload.quantity = quantity;
      }

      const response = await axios.post(`${API_URL}/createOrder`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      if (response.data.paymentUrl) {
        window.location.href = response.data.paymentUrl;
      } else {
        alert("Order creation failed.");
        setPlacingOrder(false);
      }
    } catch (error) {
      console.error("Error during checkout:", error);
      alert("Error processing checkout.");
      setPlacingOrder(false);
    }
  };

  return (
    <div className="container mt-27 mb-20 mx-auto px-4 md:px-10 py-6 mt-20">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Address & Order Summary */}
        <div className="md:col-span-2 space-y-6">
          {/* Addresses */}
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h2 className="text-xl font-bold mb-4">Delivery Address</h2>
            {addresses.length > 0 ? (
              addresses.map((address, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg cursor-pointer transition ${
                    selectedAddress === index
                      ? "border-2 border-blue-500 bg-blue-50"
                      : "border border-gray-200"
                  }`}
                  onClick={() => setSelectedAddress(index)}
                >
                  <p className="font-semibold">{address.customerName}</p>
                  <p className="text-sm text-gray-600">{`${address.addressLine1}, ${address.City}, ${address.State} - ${address.Pincode}`}</p>
                  <p className="text-sm text-gray-600">{address.phone}</p>
                </div>
              ))
            ) : (
              <p className="text-gray-500">No saved addresses</p>
            )}
          </div>


          {addresses.length < 3 && (
  <div className="mt-4">
    <button
      onClick={() =>
        navigate(`/account?redirect=/checkout${productId ? `&productId=${productId}&quantity=${quantity}` : ""}`)
      }
      className="text-blue-600 underline text-sm font-medium"
    >
      + Add Address
    </button>
  </div>
)}


          {/* Order Summary */}
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h2 className="text-xl font-bold mb-4">Order Summary</h2>
            {productDetails.length > 0 ? (
              <div className="space-y-4">
                {productDetails.map((item, i) => (
                  <div key={i} className="flex items-center gap-4 border-b pb-4">
                    <img src={item.thumbnail} alt={item.name} className="w-20 h-20 rounded-md object-cover" />
                    <div className="flex-1">
                      <p className="font-semibold">{item.name}</p>
                      <p className="text-gray-600 text-sm">₹{item.discountPrice} × {item.quantity}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No items in cart</p>
            )}
          </div>
        </div>

        {/* Price & Checkout */}
        <div className="bg-white p-6 rounded-lg shadow-md h-fit border">
          <h2 className="text-xl font-bold mb-4">Price Details</h2>
          <div className="space-y-2 text-gray-700 mb-6">
          <div className="flex justify-between">

          <p className="text-sm text-gray-700">Total {productDetails.length} item(s)</p>
      <div className="flex items-center gap-2">
        <p className="text-lg font-bold text-gray-900">₹{productDetails.reduce((acc, item) => acc + item.discountPrice * item.quantity, 0)}</p>
        {/* <p className="text-sm text-red-500 line-through">+ ₹50</p>
        <p className="text-sm text-green-600 font-semibold">Free Delivery</p> */}
      </div>
      </div>

          <div className="flex justify-between">
  <span>Delivery Fee</span>
  <div className="text-right">

    <span className="line-through text-gray-400 mr-1">₹50</span>
    <span className="text-green-600 font-semibold">Free</span>
  </div>
</div>


            {/* You can add more price breakdowns here */}
          </div>

          <button
            onClick={() => handleCheckout(productId, parseInt(quantity))}
            disabled={placingOrder}
            className={`w-full py-3 hidden md:block text-lg font-bold rounded-md transition ${
              placingOrder
                ? "bg-yellow-300 cursor-not-allowed"
                : "bg-yellow-500 hover:bg-yellow-600 text-white"
            }`}
          >
            {placingOrder ? "Initiating Payment..." : "Place Order"}
          </button>
        </div>
      </div>


      {/* ✅ Floating Bottom Bar for Mobile Devices */}
{productDetails.length > 0 && (
  <div className="fixed bottom-0 left-0 w-full bg-white border-t border-gray-300 p-4 flex justify-between items-center md:hidden z-50">
    <div>
      <p className="text-sm text-gray-700">{productDetails.length} item(s)</p>
      <div className="flex items-center gap-2">
        <p className="text-lg font-bold text-gray-900">₹{productDetails.reduce((acc, item) => acc + item.discountPrice * item.quantity, 0)}</p>
        {/* <p className="text-sm text-red-500 line-through">+ ₹50</p>
        <p className="text-sm text-green-600 font-semibold">Free Delivery</p> */}
      </div>
    </div>

    <button
      disabled={placingOrder}
      onClick={() => handleCheckout(productId, parseInt(quantity))}
      className={`px-5 py-2 rounded-md font-bold text-white transition ${
        placingOrder ? "bg-yellow-300 cursor-not-allowed" : "bg-yellow-500 hover:bg-yellow-600"
      }`}
    >
      {placingOrder ? "Initiating..." : "Place Order →"}
    </button>
  </div>
)}



    </div>
  );
};

export default Checkout;
